import { cn } from '@/lib/utils'

/**
 * v2 · Configurator progress — the segmented bar from the step
 * header: one 4px bar per step, ink for done/current, raised gray
 * for what's still owed. The `n/4` counter sits at the header's far
 * edge in tertiary.
 */

interface StepRailProps {
  steps: string[]
  /** Zero-based index of the current step. */
  current: number
  className?: string
}

/** Segmented rail — one bar per step, filled up to and including current. */
function StepRail({ steps, current, className }: StepRailProps) {
  return (
    <div
      className={cn('flex flex-1 gap-1.5', className)}
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={steps.length}
      aria-valuenow={current + 1}
      aria-valuetext={`${current + 1} of ${steps.length} — ${steps[current]}`}
    >
      {steps.map((step, i) => (
        <span
          key={step}
          className={cn(
            'h-1 flex-1 rounded-[2px]',
            i <= current ? 'bg-ink' : 'bg-surface-sunk'
          )}
        />
      ))}
    </div>
  )
}

/** `1/4` counter, sized to a 44×44 tap box's worth of gutter. */
function StepCounter({ current, total, className }: { current: number; total: number; className?: string }) {
  return (
    <span
      className={cn(
        'flex size-11 items-center justify-center text-[13px] leading-none text-ink-faint tabular-nums',
        className
      )}
    >
      {current + 1}/{total}
    </span>
  )
}

/** Named markers for the desktop configurator bar — sentence case. */
function StepMarkers({ steps, current, className }: StepRailProps) {
  return (
    <div className={cn('flex items-center gap-4 text-[13px] font-medium', className)}>
      {steps.map((step, i) => (
        <span
          key={step}
          aria-current={i === current ? 'step' : undefined}
          className={i === current ? 'font-semibold text-ink' : 'text-ink-faint'}
        >
          {step}
        </span>
      ))}
    </div>
  )
}

export { StepRail, StepCounter, StepMarkers }
